import React, { useContext } from 'react';

import { VehicleLookupContext } from 'Context/vehicleLookup';

import { getThumbnailImage } from 'Utilities/helpers';
import { PremiumOffer } from 'Utilities/profiling/@types';
import { Vehicle, VehicleLookupState } from 'Utilities/vehicles/@types';
import useGetVehicleEnquiry from 'Utilities/vehicles/hooks/useGetVehicleEnquiry';

import {
  DocumentCaptureOfferContext,
  DocumentCaptureOfferContextType,
} from '../../DocumentCaptureContexts';

export type OfferSummaryContext = {
  dpVehicleId?: number;
  isEnquiryLoaded: boolean;
  offer: {
    acceptedBidDealerName?: string;
    acceptedBidValue?: number;
    closingBidId?: number;
    keepPrivatePlate?: boolean;
    offerId?: number;
    reservePrice?: number | null | undefined;
  };
  seller: {
    isBereavement?: boolean;
  };
  vehicle: Partial<Pick<Vehicle, 'fuel' | 'manufactureYear' | 'model' | 'transmission' | 'vrm'>> & {
    colour?: string;
    image?: string;
    make?: string;
    mileage?: number;
    vrmPretty?: string;
  };
}

const getVehicleData = (
  vehicle: VehicleLookupState,
  offer: DocumentCaptureOfferContextType,
): OfferSummaryContext['vehicle'] => {
  const colour = vehicle?.colour;
  const mileage = offer?.data?.basic_details?.mileage;
  const manufactureYear = vehicle?.manufactureYear || vehicle?.year;
  const fuel = vehicle?.fuel;
  const transmission = vehicle?.transmission;
  const make = vehicle?.make?.display_name || vehicle?.make?.name;
  const model = vehicle?.model;
  const vrm = vehicle?.vrm;
  const vrmPretty = vehicle?.vrm_pretty;

  const images = offer.vehicleImages;

  const image = getThumbnailImage(images);

  return {
    colour,
    fuel,
    image,
    make,
    manufactureYear,
    mileage,
    model,
    transmission,
    vrm,
    vrmPretty,
  };
};

const getSellerData = (offer: DocumentCaptureOfferContextType): OfferSummaryContext['seller'] => {
  const isBereavement = offer?.data?.seller?.keeperType === 'probate';

  return {
    isBereavement,
  };
};

const getOfferData = (offer: DocumentCaptureOfferContextType, vehicleEnquiry: PremiumOffer | undefined): OfferSummaryContext['offer'] => {
  const reservePrice = vehicleEnquiry?.sellerWillAcceptPrice;
  const acceptedBidValue = offer?.acceptedBid?.value;
  const acceptedBidDealerName = offer?.acceptedBid?.dealer?.name;
  const keepPrivatePlate = offer?.data?.basic_details?.keepPrivatePlate;
  const closingBidId = offer?.closingBid?.id;
  const offerId = offer?.id;

  return {
    acceptedBidDealerName,
    acceptedBidValue,
    closingBidId,
    keepPrivatePlate,
    offerId,
    reservePrice,
  };
};

const OfferSummaryContext = React.createContext<OfferSummaryContext>({
  dpVehicleId: undefined,
  isEnquiryLoaded: false,
  offer: {},
  seller: {},
  vehicle: {},
});

export const OfferSummaryContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const offer = useContext(DocumentCaptureOfferContext);

  const { vehicleLookupState } = useContext(VehicleLookupContext);

  const vehicle = vehicleLookupState as Vehicle;

  const { enquiry: vehicleEnquiry, isLoaded: isEnquiryLoaded } = useGetVehicleEnquiry({ vehicle });

  return (
    <OfferSummaryContext.Provider
      value={{
        dpVehicleId: vehicleEnquiry?.vehicleId,
        isEnquiryLoaded,
        offer: getOfferData(offer, vehicleEnquiry),
        seller: getSellerData(offer),
        vehicle: getVehicleData(vehicle, offer),
      }}
    >
      {children}
    </OfferSummaryContext.Provider>
  );
};

export const useOfferSummary = () => useContext(OfferSummaryContext);
